import { Cached } from '@mui/icons-material';
import { Button, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useFormikContext } from 'formik';
import type { ReactElement } from 'react';

import type { QuoteFormikType } from '@core/formik/quote';
import { hasValue } from '@core/utils/equality';
import { SetFormikValue } from '@core/utils/formikHelpers';
import { Input } from '@web/components/Input/Input';
import { LoadingSpinner } from '@web/components/LoadingSpinner/LoadingSpinner';
import { useSlabQuery } from '@web/hooks/useSlabQuery';

import { isQuoteConfigSynced } from '../utils';

export const TermsSection = (): ReactElement => {
  const formikBag = useFormikContext<QuoteFormikType>();

  const {
    data: project,
    isLoading: isLoadingProject,
    isError: isErrorProject,
  } = useSlabQuery(
    'GET project by ID',
    {
      pathParams: {
        id: formikBag.values.project.id ?? '',
      },
    },
    {
      enabled: formikBag.values.project.id !== null,
    },
  );

  const quoteConfigId = formikBag.values.quoteConfig?.id;
  const {
    isLoading: isLoadingQuoteConfig,
    isError: isErrorQuoteConfig,
    data: curQuoteConfig,
  } = useSlabQuery(
    'GET quote config by ID',
    { pathParams: { id: quoteConfigId ?? '' } },
    { enabled: hasValue(quoteConfigId) },
  );

  if (isLoadingProject || isLoadingQuoteConfig) {
    return <LoadingSpinner />;
  }

  if (isErrorProject || isErrorQuoteConfig) {
    return (
      <Box>
        <Typography variant='h5'>Error loading terms.</Typography>
      </Box>
    );
  }

  const { areTermsSynced } = isQuoteConfigSynced(
    curQuoteConfig,
    formikBag.values.additionalProductsByPlants,
    formikBag.values.terms,
  );
  const tip = ((): ReactElement | null => {
    if (areTermsSynced) {
      return null;
    }
    return (
      <Box pb='0.5rem'>
        <Typography variant='body3'>
          Current terms are out-of-sync with selected quote configuration.
        </Typography>
      </Box>
    );
  })();

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'end',
        }}
      >
        <Tooltip title='Clears the current terms and reloads from project' placement='top-start'>
          <Button
            onClick={(): void => {
              SetFormikValue(formikBag, 'terms', project?.terms ?? null);
            }}
          >
            <Cached />
          </Button>
        </Tooltip>
      </Box>
      <Box>
        {tip}
        <Input type='textarea' name='terms' />
      </Box>
    </>
  );
};
